import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { BudgetUsageTable } from '@/components/dashboard/BudgetUsageTable';
import {
  buildCampaignNameMap,
  buildPortfolioNameMap,
  dashboardDateRangeToStreamTime,
  filterBudgetUsageEvents,
  formatBudgetUsageDateTime,
  summarizeBudgetUsageEvents,
} from '@/utils/budgetUsage';
import type { Portfolio } from '@/types/portfolios';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

const BUDGET_USAGE_LIMIT = 500;

type ScopeFilter = 'ALL' | 'CAMPAIGN' | 'PORTFOLIO';
type MinUsageFilter = 'ALL' | '50' | '80' | '100';
type ViewMode = 'events' | 'latestPerScope';

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-foreground-muted">{hint}</p> : null}
    </Card>
  );
}

export function BudgetUsageSection({
  profileId,
  startDate,
  endDate,
  campaigns,
  allowedCampaignIds = null,
  invalidRange,
}: {
  profileId: string;
  startDate: string;
  endDate: string;
  campaigns: SpCampaignNormalized[];
  allowedCampaignIds?: string[] | null;
  invalidRange?: boolean;
}) {
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('ALL');
  const [minUsageFilter, setMinUsageFilter] = useState<MinUsageFilter>('ALL');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('latestPerScope');

  const streamTime = useMemo(
    () => dashboardDateRangeToStreamTime(startDate, endDate),
    [startDate, endDate]
  );

  const { data: portfoliosData } = useQuery({
    queryKey: ['profile-portfolios', profileId],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success || !res.data) throw new Error('Failed to load portfolios');
      return res.data;
    },
    enabled: Boolean(profileId),
    staleTime: 60_000,
  });

  const {
    data: budgetUsageData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      'stream-budget-usage',
      profileId,
      streamTime.startTime,
      streamTime.endTime,
      BUDGET_USAGE_LIMIT,
      viewMode,
    ],
    queryFn: async () => {
      const res = await adsApi.getStreamBudgetUsage(profileId, {
        startTime: streamTime.startTime,
        endTime: streamTime.endTime,
        limit: BUDGET_USAGE_LIMIT,
        view: viewMode,
      });
      if (!res.success || !res.data) throw new Error('Failed to load budget usage alerts');
      return res.data;
    },
    enabled: Boolean(profileId) && !invalidRange,
    staleTime: 30_000,
  });

  const campaignNames = useMemo(() => buildCampaignNameMap(campaigns), [campaigns]);
  const portfolioNames = useMemo(
    () => buildPortfolioNameMap((portfoliosData?.portfolios ?? []) as Portfolio[]),
    [portfoliosData]
  );

  const events = budgetUsageData?.rows ?? [];
  const summary = useMemo(
    () => summarizeBudgetUsageEvents(events, budgetUsageData?.rowCount ?? 0, BUDGET_USAGE_LIMIT),
    [events, budgetUsageData?.rowCount]
  );

  const filteredEvents = useMemo(() => {
    const minUsage =
      minUsageFilter === 'ALL' ? null : Number(minUsageFilter);
    return filterBudgetUsageEvents(events, {
      scopeType: scopeFilter,
      minUsagePercent: minUsage,
      search,
      campaignNames,
      portfolioNames,
      allowedCampaignIds,
    });
  }, [
    events,
    scopeFilter,
    minUsageFilter,
    search,
    campaignNames,
    portfolioNames,
    allowedCampaignIds,
  ]);

  const displayEvents = filteredEvents;

  const streamPath = `/accounts/${encodeURIComponent(profileId)}/campaigns/stream`;

  if (invalidRange) {
    return (
      <p className="text-body-sm text-foreground-muted">
        Select a valid date range to load budget usage alerts.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-foreground-muted">
        <Spinner className="h-4 w-4" />
        Loading budget usage alerts…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-3 text-body-sm text-error-ink">
        {error instanceof Error ? error.message : 'Failed to load budget usage alerts.'}
        <button type="button" onClick={() => void refetch()} className="ml-3 font-medium underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-body-sm text-foreground-muted">
        Near real-time threshold notifications from Amazon Marketing Stream (~5% increments). This
        is not the same as report spend or hourly budget run history.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Entities at risk"
          value={String(summary.entitiesAtRisk)}
          hint="Latest usage ≥ 80% per entity"
        />
        <SummaryCard
          label="Out-of-budget signals"
          value={String(summary.outOfBudgetSignals)}
          hint="Events ≥ 95% in range"
        />
        <SummaryCard
          label="Latest alert"
          value={
            summary.latestAlertAt ? formatBudgetUsageDateTime(summary.latestAlertAt) : '—'
          }
        />
        <SummaryCard
          label="Events loaded"
          value={String(summary.eventCount)}
          hint={
            summary.hitLimit
              ? `Showing up to ${BUDGET_USAGE_LIMIT} events`
              : viewMode === 'latestPerScope'
                ? 'One latest row per campaign/portfolio'
                : 'Full event log in table below'
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="budget-usage-view" className="text-body-sm text-foreground-muted">
            View
          </label>
          <select
            id="budget-usage-view"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            <option value="latestPerScope">Latest per entity</option>
            <option value="events">Full event log</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="budget-usage-scope" className="text-body-sm text-foreground-muted">
            Scope
          </label>
          <select
            id="budget-usage-scope"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            <option value="ALL">All</option>
            <option value="CAMPAIGN">Campaign</option>
            <option value="PORTFOLIO">Portfolio</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="budget-usage-min" className="text-body-sm text-foreground-muted">
            Min usage %
          </label>
          <select
            id="budget-usage-min"
            value={minUsageFilter}
            onChange={(e) => setMinUsageFilter(e.target.value as MinUsageFilter)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            <option value="ALL">Any</option>
            <option value="50">≥ 50%</option>
            <option value="80">≥ 80%</option>
            <option value="100">≥ 100%</option>
          </select>
        </div>

        <div className="min-w-[200px] flex-1">
          <label htmlFor="budget-usage-search" className="sr-only">
            Search budget usage
          </label>
          <input
            id="budget-usage-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or ID"
            autoComplete="off"
            className="w-full rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>

        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="rounded-lg border border-border px-3 py-2 text-body-sm text-foreground-muted hover:bg-surface-muted disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {events.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface-muted/40 px-4 py-6 text-body-sm text-foreground-muted">
          <p>No budget-usage events in this date range.</p>
          <p className="mt-2">
            Amazon sends sparse threshold updates (~5% steps). Low spend or a missing{' '}
            <code className="text-xs">budget-usage</code> stream subscription can also mean no rows.
          </p>
          <p className="mt-2">
            Check stream health on the{' '}
            <Link to={streamPath} className="font-medium text-brand hover:underline">
              Dayparting
            </Link>{' '}
            page.
          </p>
        </div>
      ) : (
        <BudgetUsageTable
          events={displayEvents}
          profileId={profileId}
          campaignNames={campaignNames}
          portfolioNames={portfolioNames}
          emptyMessage={
            viewMode === 'latestPerScope'
              ? 'No entities match the current filters.'
              : 'No events match the current filters.'
          }
        />
      )}
    </div>
  );
}
